import {
  AfterViewInit,
  ChangeDetectionStrategy,
  Component,
  EventEmitter,
  OnDestroy,
  OnInit,
  ViewChild,
} from '@angular/core';
import { FormGroup } from '@angular/forms';
import { NgSelectComponent } from '@ng-select/ng-select';
import { CUSTOM_ICON_TYPE } from '@shared/models';
import { DeviceService } from '@shared/services';
import { AddressSuggestionsService, GeocodeService, YmapsService } from '@yandex-maps/feature';
import { BehaviorSubject, Observable, Subscription } from 'rxjs';
import { concatMap, debounceTime, filter, map, take } from 'rxjs/operators';
import { DeliveryAddressDialogComponentService } from './delivery-address-dialog.component.service';

@Component({
  selector: 'sd-delivery-address-dialog',
  templateUrl: './delivery-address-dialog.component.html',
  styleUrls: ['./delivery-address-dialog.component.scss'],
  providers: [DeliveryAddressDialogComponentService, AddressSuggestionsService],
  changeDetection: ChangeDetectionStrategy.OnPush,
  standalone: false,
})
export class DeliveryAddressDialogComponent implements OnInit, AfterViewInit, OnDestroy {
  @ViewChild('ngSelect') ngSelect: NgSelectComponent;

  form: FormGroup;
  selectTypeahead = new EventEmitter<string>();
  isHeightLimit$ = new BehaviorSubject(false);
  isMobile$: Observable<boolean>;
  iconType = CUSTOM_ICON_TYPE;

  heightLimit = [
    { value: 2.2, name: '2,2 м.' },
    { value: 3, name: '3 м.' },
  ];

  private subscription = new Subscription();

  constructor(
    public ymapsService: YmapsService,
    public suggestionsService: AddressSuggestionsService,
    private geocodeService: GeocodeService,
    public componentService: DeliveryAddressDialogComponentService,
    private deviceService: DeviceService,
  ) {}

  ngOnInit(): void {
    this.isMobile$ = this.deviceService.isMobileSmDown$;
    this.form = this.componentService.form;

    const heightLimitControl = this.form.get('heightLimit');
    const hasHeightLimitControl = this.form.get('hasHeightLimit');
    this.subscription.add(
      hasHeightLimitControl.valueChanges.subscribe((val) => {
        this.isHeightLimit$.next(val);

        if (val && !heightLimitControl.value) {
          heightLimitControl.setValue(this.heightLimit[0].value);
        }
        if (!val && heightLimitControl.value) {
          heightLimitControl.setValue(null);
        }
      }),
    );

    this.subscription.add(
      this.componentService.addressData$.subscribe((data) => {
        this.form.setValue(data);
      }),
    );
  }

  ngAfterViewInit(): void {
    this.subscription.add(
      this.selectTypeahead.pipe(debounceTime(700)).subscribe(this.suggestionsService.loadSuggestions),
    );

    this.subscription.add(
      this.componentService.addressData$.subscribe((data) => {
        this.ngSelect.searchInput.nativeElement.value = data.address;
      }),
    );

    this.subscription.add(
      this.componentService.activeAddressId$
        .pipe(
          take(1),
          filter<string>(Boolean),
          concatMap(() => this.componentService.addressData$),
          concatMap((data) =>
            this.geocodeService.getGeoObject(data.address).pipe(
              map((geocodeResult) => {
                const point = data.addressAdditionalData?.point;
                const exactPoint = point?.latitude && point?.longitude ? point : undefined;

                return {
                  geocodeResult,
                  exactPoint,
                };
              }),
            ),
          ),
          take(1),
        )
        .subscribe(({ geocodeResult, exactPoint }) => {
          const coordinates: ymaps.Coordinate = exactPoint
            ? [exactPoint.latitude, exactPoint.longitude]
            : geocodeResult.geometry.getCoordinates();

          this.componentService.setGeoObject({ geocodeResult, exactPoint });
          this.componentService.pintMapToCoord(coordinates);
        }),
    );
  }

  onBlurSelect(e: FocusEvent): void {
    const input = e.target as HTMLInputElement;
    this.setAddressValue(input.value);
  }

  onChangeSelect(suggestion?: string): void {
    if (!suggestion) {
      return;
    }

    this.form.get('address').disable();
    this.ngSelect.searchInput.nativeElement.value = suggestion;
    this.geocodeService.getGeoObject(suggestion).subscribe({
      next: (data) => {
        this.setAddressValue(suggestion);
        this.componentService.setGeoObject({ geocodeResult: data });
        this.componentService.pintMapToCoord(data.geometry.getCoordinates());
      },
      error: () => this.setAddressValue(suggestion),
      complete: () => this.form.get('address').enable(),
    });
  }

  onSearchSelect({ term }: { term: string }): void {
    // Обновляем form control без writeValue в ng-select.
    // Иначе при совпадении term с bindValue подсказка становится selected,
    // и клик по ней не эмитит onChangeSelect не вызывается.
    this.form.get('address').setValue(term, { emitModelToViewChange: false });
    this.ngSelect?.writeValue(null);
  }

  setAddressValue(address: string): void {
    this.form.get('address').setValue(address);
  }

  submitAddressValue(): void {
    if (!this.form.valid || !this.form.get('address').enabled) {
      this.form.get('address').markAsDirty();

      return;
    }
    this.componentService.saveAddress(this.form.value);
  }

  ngOnDestroy(): void {
    this.subscription.unsubscribe();
  }
}
